<template>
    <div>
        <div v-if="!editable" class="text--secondary">
            {{ entry.duration }} {{ $t('night', entry.duration) }}
            <v-icon color="primary" size="small" @click="editable = true">mdi-pencil</v-icon>
        </div>
        <v-tooltip v-else :key="key" location="top">
            <template v-slot:activator="{props}">
                <v-select
                    :model-value="duration"
                    :items="items"
                    single-line
                    hide-details
                    variant="outlined"
                    density="compact"
                    :disabled="loading"
                    :loading="loading"
                    class="pe-5"
                    v-bind="props"
                    @update:model-value="change"
                >
                    <template v-slot:selection="{item}">
                        <span class="text-no-wrap">
                            {{ `${item.value} ${$t('night', item.value)}` }}
                        </span>
                    </template>
                </v-select>
            </template>
            {{ $t('tours.notification.related_offers_was_changed') }}
        </v-tooltip>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import {getSupplierIdByCode} from '@/stores/hotels/HotelsRuntimeStore'
    import {getMethod} from '~/api'
    import {salesTermsRsTransformer} from '@/utils/api-helpers'
    import {tourEntrySelectedOffer} from '~/utils/tours/tour-helpers'

    @Component
    export class HotelServiceDurationSelect extends Vue {
        @Prop({required: true}) entry
        @Prop({required: true}) offer
        @Prop({required: true}) searchRequest
        @Prop() citizenshipId

        loading = false
        key = 0
        editable = false

        async change(duration) {
            const newOfferDeltaPrice = (entry, newPrice) => ({
                amount:
                    newPrice.amount -
                    (tourEntrySelectedOffer(entry)?.notDeltaPrice.amount || 0) +
                    (tourEntrySelectedOffer(entry)?.deltaPrice.amount || 0),
                currency: newPrice.currency,
            })
            try {
                this.loading = true
                const {convertToCurrency, rooms} = this.searchRequest
                let adults = 0,
                    childrenAges = []
                for (const room of hotelsRuntimeStore.searchRequestTouristsJSON(this.searchRequest)) {
                    adults += room.adults
                    childrenAges.push(...room.childrenAges)
                }
                const startDate = this.entry.entryDate.split(' ')[0],
                    endDateObj = this.$dateFns.addDays(this.$dateFns.parseISO(startDate), duration)

                const newHotels = await Promise.all(
                    this.entry.offers.map(async ({supplierCode, cityCode, hotelCode}) => {
                        const supplierId = await getSupplierIdByCode(supplierCode)
                        const {
                            offers: [hotel],
                        } = await this.$api.searchAccommodation.get({
                            cityCode,
                            hotelCode,
                            supplierId,
                            convertToCurrency,
                            rooms,
                            startDate,
                            endDate: this.$dateFns.format(endDateObj),
                        })
                        hotel.rooms.forEach(room => {
                            room.notDeltaPrice = room.price
                            delete room.price
                        })
                        return hotel
                    })
                )
                const oldHotel = hotelsRuntimeStore.findHotelByOfferKey(this.entry.offers, this.entry.selectedOfferKey)
                const oldRoom = hotelsRuntimeStore.findRoomOffer(oldHotel.rooms, this.entry.selectedOfferKey)
                const newHotel = hotelsRuntimeStore.findSameHotel(newHotels, oldHotel)
                const newRoom = hotelsRuntimeStore.findSameRoomOffer(newHotel.rooms, oldRoom)
                const selectedOfferKey = newRoom.groupedOffers[0].offerKey
                const allOldRooms = this.entry.offers.map(({rooms}) => rooms).flat()
                const zeroDeltaPriceOldRoom =
                    allOldRooms.find(({deltaPrice: {amount}}) => amount === 0) ||
                    allOldRooms.find(({deltaPrice: {amount}}) => !amount)
                const zeroDeltaPriceNewRoom = hotelsRuntimeStore.findSameRoomOffer(
                    newHotels.map(({rooms}) => rooms).flat(),
                    zeroDeltaPriceOldRoom
                ) || {notDeltaPrice: {amount: 0}}
                newHotels.forEach(({rooms}) => {
                    rooms.forEach(room => {
                        if (room.notDeltaPrice.amount) {
                            const {amount, currency} = room.notDeltaPrice
                            room.deltaPrice = {amount: amount - zeroDeltaPriceNewRoom.notDeltaPrice.amount, currency}
                        } else {
                            room.deltaPrice = {}
                        }
                    })
                })
                const updatedEntries = [{...this.entry, selectedOfferKey, offers: newHotels, duration}]
                const oldAccommodations = this.offer.entries.filter(({type}) => ['ACCOMMODATION'].includes(type))

                for (const entry of oldAccommodations) {
                    if (this.entry.selectedOfferKey !== entry.selectedOfferKey) {
                        const prevUpdatedHotelEntry = updatedEntries.find(
                            ({entryName}) => +entryName.replace('H', '') === entry.entryName.replace('H', '') - 1
                        )

                        if (prevUpdatedHotelEntry) {
                            const updatedHotelEndDateObj = this.$dateFns.addDays(
                                this.$dateFns.parseISO(prevUpdatedHotelEntry.entryDate),
                                prevUpdatedHotelEntry.duration
                            )
                            const endDateObj = this.$dateFns.addDays(updatedHotelEndDateObj, entry.duration)
                            const newHotels = await Promise.all(
                                entry.offers.map(async ({supplierCode, cityCode, hotelCode}) => {
                                    const supplierId = await getSupplierIdByCode(supplierCode)
                                    const {
                                        offers: [hotel],
                                    } = await this.$api.searchAccommodation.get({
                                        cityCode,
                                        hotelCode,
                                        supplierId,
                                        convertToCurrency,
                                        rooms,
                                        startDate: this.$dateFns.format(updatedHotelEndDateObj),
                                        endDate: this.$dateFns.format(endDateObj),
                                    })
                                    hotel.rooms.forEach(room => {
                                        room.notDeltaPrice = room.price
                                        delete room.price
                                    })
                                    return hotel
                                })
                            )
                            const oldHotel = hotelsRuntimeStore.findHotelByOfferKey(
                                entry.offers,
                                entry.selectedOfferKey
                            )
                            const oldRoom = hotelsRuntimeStore.findRoomOffer(oldHotel.rooms, entry.selectedOfferKey)
                            const newHotel = hotelsRuntimeStore.findSameHotel(newHotels, oldHotel)
                            const newRoom = hotelsRuntimeStore.findSameRoomOffer(newHotel.rooms, oldRoom)
                            const selectedOfferKey = newRoom.groupedOffers[0].offerKey
                            const allOldRooms = this.entry.offers.map(({rooms}) => rooms).flat()
                            const zeroDeltaPriceOldRoom =
                                allOldRooms.find(({deltaPrice: {amount}}) => amount === 0) ||
                                allOldRooms.find(({deltaPrice: {amount}}) => !amount)
                            const zeroDeltaPriceNewRoom = hotelsRuntimeStore.findSameRoomOffer(
                                newHotels.map(({rooms}) => rooms).flat(),
                                zeroDeltaPriceOldRoom
                            ) || {notDeltaPrice: {amount: 0}}

                            newHotels.forEach(({rooms}) => {
                                rooms.forEach(room => {
                                    if (room.notDeltaPrice.amount) {
                                        const {amount, currency} = room.notDeltaPrice
                                        room.deltaPrice = {
                                            amount: amount - zeroDeltaPriceNewRoom.notDeltaPrice.amount,
                                            currency,
                                        }
                                    } else {
                                        room.deltaPrice = {}
                                    }
                                })
                            })
                            updatedEntries.push({
                                ...entry,
                                entryDate: newHotel.startDate,
                                selectedOfferKey,
                                offers: newHotels,
                                duration: entry.duration,
                                dayNumber: prevUpdatedHotelEntry.duration + 1,
                            })
                        }
                    }
                }

                for (const entry of this.offer.entries.filter(({type}) => ['FLIGHT', 'OWNCHARTER'].includes(type))) {
                    let needUpdate = false
                    const {itinerary} = entry.offers[0]
                    const routes = itinerary.map(({segments}) => {
                        const first = segments[0],
                            last = segments[segments.length - 1]
                        let departureDate = first.departure.date.split(' ')[0]
                        const departureDateObj = this.$dateFns.parseISO(departureDate)
                        const accommodationIndex = oldAccommodations.findIndex(accommodation =>
                            this.$dateFns.isSameDay(
                                departureDateObj,
                                this.$dateFns.parseISO(accommodation.offers[0].endDate)
                            )
                        )

                        if (accommodationIndex >= 0) {
                            const updatedAccommodation = updatedEntries.find(
                                ({entryName}) => entryName === oldAccommodations[accommodationIndex].entryName
                            )
                            if (updatedAccommodation) {
                                needUpdate = true
                                departureDate = updatedAccommodation.offers[0].endDate
                            }
                        }
                        return `${first.departure.airportCode}*-${last.arrival.airportCode}*,${departureDate}`
                    })

                    if (needUpdate) {
                        const {offers: flights} = await this.$api.searchFlight.get({
                            convertToCurrency,
                            routes,
                            adults,
                            childrenAges,
                            citizenshipId: this.citizenshipId,
                            directFlight: false,
                        })
                        const offers = flights.map(flight => {
                            flight.notDeltaPrice = flight.price
                            flight.deltaPrice = newOfferDeltaPrice(entry, flight.price)
                            delete flight.price
                            return flight
                        })
                        offers
                            .sort((a, b) => a.notDeltaPrice.amount - b.notDeltaPrice.amount)
                            .sort((a, b) => {
                                const maxStops = ({itinerary}) =>
                                        Math.max(...itinerary.map(({segments}) => segments.length)),
                                    maxStopsA = maxStops(a),
                                    maxStopsB = maxStops(b)
                                return maxStopsA === maxStopsB ? 0 : maxStopsA > maxStopsB ? 1 : -1
                            })
                        updatedEntries.push({...entry, selectedOfferKey: offers[0].offerKey, offers})
                    }
                }

                for (const entry of this.offer.entries.filter(({type}) => type === 'TRANSFER')) {
                    const startDateObj = this.$dateFns.addDays(
                        this.$dateFns.parseISO(this.offer.info.packageDate.split(' ')[0]),
                        entry.dayNumber - 1
                    )
                    const accommodationIndex = oldAccommodations.findIndex(accommodation =>
                        this.$dateFns.isSameDay(startDateObj, this.$dateFns.parseISO(accommodation.offers[0].endDate))
                    )

                    if (accommodationIndex >= 0) {
                        const updatedAccommodation = updatedEntries.find(
                            ({entryName}) => entryName === oldAccommodations[accommodationIndex].entryName
                        )

                        if (updatedAccommodation) {
                            const dateTime = updatedAccommodation.offers[0].endDate
                            const place = ({type, code}) =>
                                type === 'AIRPORT'
                                    ? code
                                    : type === 'STATION'
                                      ? code
                                      : type === 'HOTEL'
                                        ? 'hotel'
                                        : 'hotel'
                            const transfers = []
                            const offer = entry.offers[0]
                            const {products} = await getMethod('/api/searchTransfer', {
                                convertToCurrency,
                                departurePlace: place(offer.departureInfo),
                                departureCityId: offer.departureInfo.cityId,
                                departureCountryId: offer.departureInfo.countryId,
                                arrivalPlace: place(offer.arrivalInfo),
                                arrivalCityId: offer.arrivalInfo.cityId,
                                arrivalCountryId: offer.arrivalInfo.countryId,
                                adults,
                                childrenAges,
                                dateTime,
                            })
                            const product = products.find(({info}) => info.name === entry.name)

                            product.offers.map(offer => {
                                let transfer = offer
                                const {price} = salesTermsRsTransformer(transfer.salesTerms)
                                transfer.deltaPrice = {amount: 0, currency: price.currency}
                                transfer.notDeltaPrice = newOfferDeltaPrice(entry, price)
                                delete transfer.salesTerms

                                transfers.push(transfer)
                            })

                            if (transfers.length) {
                                let index = 0
                                if (entry.selectedOfferKey) {
                                    let oldSelectedOffer = entry.offers.find(
                                        ({offerKey}) => entry.selectedOfferKey === offerKey
                                    )
                                    index = transfers.findIndex(
                                        t => t?.info?.description === oldSelectedOffer?.info?.description
                                    )
                                }
                                const dayNumber = updatedAccommodation.dayNumber + updatedAccommodation.duration

                                updatedEntries.push({
                                    ...entry,
                                    ...(entry.selectedOfferKey && {
                                        selectedOfferKey: transfers[index >= 0 ? index : 0].offerKey,
                                    }),
                                    offers: transfers,
                                    dayNumber,
                                })
                            }
                        }
                    }
                }

                for (const entry of this.offer.entries.filter(({type}) => type === 'CAR')) {
                    const endDateObj = this.$dateFns.parseISO(entry.offer.dropOffDate)
                    const accommodationIndex = oldAccommodations.findIndex(accommodation =>
                        this.$dateFns.isSameDay(endDateObj, this.$dateFns.parseISO(accommodation.offers[0].endDate))
                    )

                    if (accommodationIndex >= 0) {
                        const updatedAccommodation = updatedEntries.find(
                            ({entryName}) => entryName === oldAccommodations[accommodationIndex].entryName
                        )

                        if (updatedAccommodation) {
                            const {products} = await getMethod('/api/searchCarRent', {
                                convertToCurrency,
                                dropOffCityId: entry.offer.dropOffCityId,
                                pickUpCityId: entry.offer.pickUpCityId,
                                pickUpDateTime: entry.offer.pickUpDate,
                                dropOffDateTime: updatedAccommodation.offers[0].endDate,
                                driverCitizenshipId: this.citizenshipId,
                                driverAge: 30,
                            })

                            if (products.length) {
                                const product = products.find(
                                    ({info: {modelName}}) => modelName === entry.info.modelName
                                )
                                const offer = product?.offers.find(({rateName}) => rateName === entry.offer.rateName)
                                const {price} = salesTermsRsTransformer(offer.salesTerms)
                                offer.deltaPrice = newOfferDeltaPrice(entry, price)
                                offer.notDeltaPrice = price
                                delete offer.salesTerms
                                const selectedOfferKey = offer.offerKey
                                updatedEntries.push({
                                    ...entry,
                                    ...(entry.selectedOfferKey && {selectedOfferKey}),
                                    offer,
                                    entryDate: offer.pickUpDate,
                                    info: product.info,
                                    dayNumber: updatedAccommodation.dayNumber + updatedAccommodation.duration,
                                })
                            }
                        }
                    }
                }

                toursRuntimeStore.UPDATE_ENTRIES(updatedEntries)
            } catch (e) {
                this.key++
                console.error(e)
                this.$toast.error(this.$t('error_message.something_went_wrong'))
            } finally {
                this.loading = false
            }
        }

        get duration() {
            return this.entry.duration
        }

        get items() {
            const arr = Array.from(Array(181).keys())
            arr.shift()
            return arr
        }
    }

    export default HotelServiceDurationSelect
</script>
